import {compact} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {
  ProcessFlowchartNodeDetailsType,
  ProcessFlowchartPlatformFeatureType,
  ProcessMapperTokenType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible, accessibleObjs} from 'graphql-client/InaccessibleInfo';
import {LookupConfigSpec, LookupStepSpec, LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {CidIdMap} from 'notebook/notes/NoteConstants';
import {ProcessFlowchartEditorConfigurator} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorConfigurator';
import {
  FlowchartEditorMode,
  FEATURE_OVERRIDES_FOR_EDITOR_MODE,
  isExecutionViewerMode,
  ProcessFlowchartEditorModeFeatureType,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/ProcessFlowchartEditorModeConstants';
import {
  findAllDownstreamMappableNodeIds,
  findAllUpstreamMappableNodeIds,
  isMappableNode,
} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/TraverseFlowchart';
import {isRouterNodeDetailsGQL} from 'processes/graph-builder-processes/flowchart-viz/util/getNodeDetailsTypeForGQL';
import {defaultNodeHandleIdGenerator} from 'ui-library/reactflow/nodes/SimpleBenchlingNode/sub-components/SimpleBenchlingNodeHandle';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';

import {ProcessTaskDefinitionGraphQL} from '../../../../../graphql-fragments/processes/__generated/ProcessTaskDefinitionGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {
  convertFlowchartConfigVersionToNodes,
  getNodeIdPairKeyForEdgeLabel,
} from './buildStaticNodesFromGraphQL';
import {makeDataFlowEdgeHandle, getFieldInfoFromDataFlowEdgeHandle} from './flowchartIdentifierUtils';
import isLookupConfigCustomMapping from './isLookupConfigCustomMapping';
import {populateMapFromStartSectionRows} from './mapFromStartUtils';
import {
  ProcessFlowchartEdgeType,
  FlowchartStaticEdgeData,
  CustomMappingData,
  TaskSchemaNodeSectionType,
  isCustomMappingDataEmpty,
  TransitionSource,
} from './processFlowchartConfigEditorConstants';
import {
  ProcessTaskDefinitionVersionGraphQL,
  ProcessFlowchartConfigVersionGQL,
  FieldDefinitionGraphQL,
  FieldMapperConfigGQL,
  TopLevelFieldMapperConfigGQL,
  InboundSchemaMapperGQL,
  SchemaMapperDependencyGQL,
} from './processFlowchartConfigEditorConstants';
import {
  StaticFlowchartNodeData,
  TaskSchemaStaticNodeData,
  isStaticTaskSchemaNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {getDefaultTransitionSource} from './TransitionSourceUtils';
import {getFieldPairKey, getOrCreateTransition} from './types/transition/transitionKeyUtils';
import {Transition} from './types/transition/TransitionTypes';

export function generateDeterministicEdgeId(sourceNodeId: string, targetNodeId: string): string {
  return `edge-${sourceNodeId}-${targetNodeId}`;
}

export function buildControlFlowEdge({
  clientId,
  sourceNodeId,
  targetNodeId,
  label,
}: {
  clientId?: string;
  sourceNodeId: string;
  targetNodeId: string;
  label?: string;
}): FlowchartStaticEdgeData {
  return {
    id: clientId ?? generateDeterministicEdgeId(sourceNodeId, targetNodeId),
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle: defaultNodeHandleIdGenerator(sourceNodeId, 'source'),
    targetHandle: defaultNodeHandleIdGenerator(targetNodeId, 'target'),
    type: ProcessFlowchartEdgeType.CONTROL_FLOW,
    label,
  };
}

export function buildDataFlowEdge({
  sourceNodeId,
  targetNodeId,
  fromFieldId,
  fromFieldSectionType,
  toFieldId,
  toFieldSectionType,
}: {
  sourceNodeId: string;
  targetNodeId: string;
  fromFieldId: string;
  fromFieldSectionType: TaskSchemaNodeSectionType;
  toFieldId: string;
  toFieldSectionType: TaskSchemaNodeSectionType;
}): FlowchartStaticEdgeData {
  return {
    id: uuidv4(),
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle: makeDataFlowEdgeHandle({
      nodeId: sourceNodeId,
      fieldClientId: fromFieldId,
      sectionType: fromFieldSectionType,
      handleType: 'source',
    }),
    targetHandle: makeDataFlowEdgeHandle({
      nodeId: targetNodeId,
      fieldClientId: toFieldId,
      sectionType: toFieldSectionType,
      handleType: 'target',
    }),
    type: ProcessFlowchartEdgeType.DATA_FLOW,
  };
}

/**
 * Get the first field id from a lookup config with a SCHEMA_FIELD or PROCESS_TASK_DEFINITION_FIELD lookup step.
 *
 * @param lookupConfig The lookup config to get the first field id from.
 * @returns The first field id from the lookup config, or null if no field id is found.
 */

export function getFirstFieldIdFromLookupConfig(lookupConfig: LookupConfigSpec): string | null {
  const lookupStep = lookupConfig.lookupSteps.find(
    (lookupStep) =>
      lookupStep.type === LookupStepType.SCHEMA_FIELD ||
      lookupStep.type === LookupStepType.PROCESS_TASK_DEFINITION_FIELD
  );
  if (lookupStep == null) {
    return null;
  }

  if (lookupStep.type === LookupStepType.SCHEMA_FIELD) {
    if (lookupStep.schemaField == null || !APIUtil.isSchemaField(lookupStep.schemaField)) {
      return null;
    }
    return lookupStep.schemaField;
  } else {
    return lookupStep.processTaskDefinitionFieldIdentifier;
  }
}

export interface StaticFlowchartData {
  nodesStaticData: Array<StaticFlowchartNodeData>;
  edgesStaticData: Array<FlowchartStaticEdgeData>;
  availableTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  transitions: Record<string, Transition>;
  didInitialConfigHaveConditionalMapping: boolean;
  hasChainedRouters: boolean;
  cidToIdMap: CidIdMap;
  /**
   * PTDV id → task schema id for PTDVs referenced by schema mapper configs.
   * Covers old/non-latest PTDVs (optional-transition targets) not represented as flowchart
   * nodes or task definition latest versions, so their schema can still be resolved.
   */
  schemaMapperPtdvIdToTaskSchemaId?: Record<string, string>;
}

interface BuildStaticDataFromFlowchartConfigGraphQLRunnerArgs {
  flowchartConfigVersion: ProcessFlowchartConfigVersionGQL | null;
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  rootTaskDefinition: ProcessTaskDefinitionGraphQL & {
    latestVersion: ProcessTaskDefinitionVersionGraphQL;
  };
  allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  editorMode: FlowchartEditorMode;
}

interface ProcessTaskDefinitionVersionIdReference {
  __typename: 'ProcessTaskDefinitionVersionNode';
  id: string;
}

/**
 * The subset of a PTDV that the edge/transition builders actually read. Viewer-mode mapper refs are
 * intentionally id-only; editor-mode refs carry field definitions for data-flow and optional-transition
 * hydration.
 */
type ResolvableProcessTaskDefinitionVersion = ProcessTaskDefinitionVersionIdReference &
  Partial<
    Pick<
      ProcessTaskDefinitionVersionGraphQL,
      'processTaskSchema' | 'processTaskDefinition' | 'inputFieldDefinitions' | 'outputFieldDefinitions'
    >
  >;

/**
 * A PTDV carries usable field definitions only when the fragment that produced it selected them. Slim
 * viewer PTDVs (ProcessTaskDefinitionVersionForViewer) and thin id-only refs leave both undefined.
 */
function hasResolvedFieldDefinitions(ptdv: ResolvableProcessTaskDefinitionVersion): boolean {
  return ptdv.inputFieldDefinitions != null || ptdv.outputFieldDefinitions != null;
}

class BuildStaticDataFromFlowchartConfigGraphQLRunner {
  private readonly flowchartConfigVersion: ProcessFlowchartConfigVersionGQL | null;
  private readonly rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  private readonly rootTaskDefinition: ProcessTaskDefinitionGraphQL & {
    latestVersion: ProcessTaskDefinitionVersionGraphQL;
  };
  private readonly allTaskSchemas: Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>;
  private readonly flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  private readonly editorMode: FlowchartEditorMode;

  private nodeClientIdToTaskDefinitionVersion: Record<string, ProcessTaskDefinitionVersionGraphQL>;
  private fullProcessTaskDefinitionVersionsById: Map<string, ResolvableProcessTaskDefinitionVersion>;

  private controlFlowEdges: Array<FlowchartStaticEdgeData>;
  private dataFlowEdges: Array<FlowchartStaticEdgeData>;
  private nodesStaticData: Array<StaticFlowchartNodeData>;
  private nodesById: Record<string, StaticFlowchartNodeData>;
  private edgesStaticData: Array<FlowchartStaticEdgeData>;
  private mapFromStartFieldIdsByNode: Record<string, Array<string>>;
  private transitions: Record<string, Transition>;
  private nodeIdPairToEdgeLabel: Record<string, string>;
  private cidToIdMap: CidIdMap;

  // Maps from field identifier to client identifier for data flow edges
  private fieldIdentifierToFieldClientId: Record<string, string>;

  constructor(args: BuildStaticDataFromFlowchartConfigGraphQLRunnerArgs) {
    this.flowchartConfigVersion = args.flowchartConfigVersion;
    this.rootTaskSchema = args.rootTaskSchema;
    this.rootTaskDefinition = args.rootTaskDefinition;
    this.allTaskSchemas = args.allTaskSchemas;
    this.flowchartEditorConfigurator = args.flowchartEditorConfigurator;
    this.editorMode = args.editorMode;

    this.cidToIdMap = {};

    this.nodeClientIdToTaskDefinitionVersion = {};
    this.fullProcessTaskDefinitionVersionsById = new Map();
    this.controlFlowEdges = [];
    this.dataFlowEdges = [];
    this.fieldIdentifierToFieldClientId = {};

    this.nodesStaticData = [];
    this.nodesById = {};
    this.edgesStaticData = [];
    this.mapFromStartFieldIdsByNode = {};
    this.transitions = {};
    this.nodeIdPairToEdgeLabel = {};
  }

  run(): StaticFlowchartData {
    this.extractTaskDefinitionVersions();
    this.buildFullProcessTaskDefinitionVersionsById();
    this.buildFieldIdentifierToClientIdMapping();
    const nodeResult = convertFlowchartConfigVersionToNodes({
      flowchartConfigVersion: this.flowchartConfigVersion,
      rootTaskDefinition: this.rootTaskDefinition,
      rootTaskSchema: this.rootTaskSchema,
      allTaskSchemas: this.allTaskSchemas,
      editorMode: this.editorMode,
    });
    this.nodesStaticData = nodeResult.nodesStaticData;
    this.cidToIdMap = {...this.cidToIdMap, ...nodeResult.cidToIdMap};
    this.nodeIdPairToEdgeLabel = nodeResult.nodeIdPairToEdgeLabel;
    this.buildNodesById();
    this.convertFlowchartConfigVersionToEdges();
    this.setFallbackTransitionSources();
    this.markOptionalTransitions();
    const hasChainedRouters = this.getHasChainedRouters();

    return {
      nodesStaticData: this.nodesStaticData,
      edgesStaticData: this.edgesStaticData,
      availableTaskSchemas: accessibleObjs(
        this.flowchartConfigVersion?.availableSchemas ?? []
      ) as Array<ProcessTaskSchemaWithoutFlowchartConfigGraphQL>,
      transitions: this.transitions,
      didInitialConfigHaveConditionalMapping: this.getDidInitialConfigHaveConditionalMapping(),
      hasChainedRouters,
      cidToIdMap: this.cidToIdMap,
      schemaMapperPtdvIdToTaskSchemaId: this.buildSchemaMapperPtdvIdToTaskSchemaId(),
    };
  }

  /**
   * Builds a map from schema-mapper-config PTDV ids to their task schema ids.
   * Uses processTaskSchema.id from the PTDV, which is available in the editor fragment
   * (ProcessTaskDefinitionVersionForEditorGraphQL). Covers old/non-latest PTDVs referenced
   * as optional-transition targets whose id is not the latest version of any task definition.
   */
  private buildSchemaMapperPtdvIdToTaskSchemaId(): Record<string, string> {
    const mapping: Record<string, string> = {};
    for (const schemaMapperConfig of this.flowchartConfigVersion?.schemaMapperConfigs ?? []) {
      const toPTDV = schemaMapperConfig.toProcessTaskDefinitionVersion;
      if (isInaccessible(toPTDV)) {
        continue;
      }
      const fullPTDV = this.fullProcessTaskDefinitionVersionsById.get(toPTDV.id);
      const processTaskSchema = fullPTDV?.processTaskSchema;
      if (processTaskSchema != null && !isInaccessible(processTaskSchema)) {
        mapping[toPTDV.id] = processTaskSchema.id;
      }
    }
    return mapping;
  }

  /**
   * For single-source requests, checks whether an existing transition (e.g. a merging transition)
   * already covers this source-target pair before creating a new one. This works because we do not allow
   * transitions that are a subset of each other.
   */
  private getOrCreateTransition(sourcePTDVIds: Array<string>, targetPTDVId: string): Transition {
    if (sourcePTDVIds.length === 1) {
      for (const transition of Object.values(this.transitions)) {
        if (transition.targetPTDVId === targetPTDVId && transition.sourcePTDVIds.includes(sourcePTDVIds[0])) {
          return transition;
        }
      }
    }

    return getOrCreateTransition({
      sourcePTDVIds,
      targetPTDVId,
      transitions: this.transitions,
      isPlanned: true,
    }).transition;
  }

  private buildNodesById(): void {
    for (const node of this.nodesStaticData) {
      this.nodesById[node.id] = node;
    }
  }

  private getNodeById(nodeId: string): StaticFlowchartNodeData | undefined {
    return this.nodesById[nodeId];
  }

  private getTaskSchemaNodeById(nodeId: string): TaskSchemaStaticNodeData | undefined {
    const node = this.nodesById[nodeId];
    return node && isStaticTaskSchemaNodeData(node) ? node : undefined;
  }

  private getRootNode(): TaskSchemaStaticNodeData | undefined {
    return this.nodesStaticData.find(
      (node): node is TaskSchemaStaticNodeData =>
        isStaticTaskSchemaNodeData(node) &&
        node.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS
    );
  }

  private getHasChainedRouters(): boolean {
    for (const edge of this.flowchartConfigVersion?.templateFlowchart?.edgeConfigs ?? []) {
      const sourceNode = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs.find(
        (node) => node.id === edge.fromNodeConfig.id
      );
      const targetNode = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs.find(
        (node) => node.id === edge.toNodeConfig.id
      );
      if (sourceNode == null || targetNode == null) {
        continue;
      }
      if (isRouterNodeDetailsGQL(sourceNode.nodeDetails) && isRouterNodeDetailsGQL(targetNode.nodeDetails)) {
        return true;
      }
    }
    return false;
  }

  private extractTaskDefinitionVersions(): void {
    // TODO: See if this can be moved into the convertGQLFlowchartConfigToClientFlowchart function
    // so that we don't have to extract the task definition versions first
    const nodeConfigs = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [];
    for (const nodeConfig of nodeConfigs) {
      const nodeDetails = nodeConfig.nodeDetails;
      if (
        nodeDetails.__typename === 'ProcessTaskNodeDetailsNode' ||
        nodeDetails.__typename === 'ProcessRootNodeDetailsNode' ||
        nodeDetails.__typename === 'ProcessOutputNodeDetailsNode'
      ) {
        const processTaskDefinitionVersion = nodeDetails.processTaskDefinitionVersion;
        if (isInaccessible(processTaskDefinitionVersion)) {
          continue;
        }
        // All three node detail types have structurally identical PTDVs; cast to the canonical type.
        this.nodeClientIdToTaskDefinitionVersion[nodeConfig.id] =
          processTaskDefinitionVersion as ProcessTaskDefinitionVersionGraphQL;
      }
    }
  }

  private buildFullProcessTaskDefinitionVersionsById(): void {
    for (const taskDefinitionVersion of Object.values(this.nodeClientIdToTaskDefinitionVersion)) {
      this.addFullProcessTaskDefinitionVersion(taskDefinitionVersion);
    }
    this.addFullProcessTaskDefinitionVersion(this.rootTaskDefinition.latestVersion);

    const availableTaskDefinitions =
      (
        this.flowchartConfigVersion as {
          availableTaskDefinitions?: Array<
            ProcessTaskDefinitionGraphQL | {__typename: 'InaccessibleInfoNode'}
          > | null;
        } | null
      )?.availableTaskDefinitions ?? [];
    for (const taskDefinition of availableTaskDefinitions ?? []) {
      if (!isInaccessible(taskDefinition)) {
        this.addFullProcessTaskDefinitionVersion(taskDefinition.latestVersion);
      }
    }

    // Schema-mapper configs can reference PTDVs that are neither flowchart nodes nor in
    // availableTaskDefinitions (e.g. optional-transition targets). Their from/to PTDV refs carry the
    // field definitions needed to hydrate field-level mappings, so register them as a fallback. The
    // `has` guard in addFullProcessTaskDefinitionVersion ensures node/root/available PTDVs (which may
    // carry richer data) registered above are never overwritten by these.
    for (const schemaMapperConfig of this.flowchartConfigVersion?.schemaMapperConfigs ?? []) {
      this.addFullProcessTaskDefinitionVersion(schemaMapperConfig.toProcessTaskDefinitionVersion);
      for (const fieldMapperConfig of schemaMapperConfig.schemaFieldMapperConfigs) {
        this.addFullProcessTaskDefinitionVersion(fieldMapperConfig.fromProcessTaskDefinitionVersion);
      }
    }
  }

  private addFullProcessTaskDefinitionVersion(
    processTaskDefinitionVersion:
      | ResolvableProcessTaskDefinitionVersion
      | {__typename: 'InaccessibleInfoNode'}
      | null
      | undefined
  ): void {
    if (processTaskDefinitionVersion == null || isInaccessible(processTaskDefinitionVersion)) {
      return;
    }
    // Prefer the registration that actually carries field definitions, regardless of order. In
    // viewer mode, templateFlowchart node PTDVs are slim (ProcessTaskDefinitionVersionForViewer has
    // no field definitions); the full field definitions arrive separately via availableTaskDefinitions
    // and schema-mapper-config from/to PTDVs. A slim entry must never shadow a full one for the same id.
    const existing = this.fullProcessTaskDefinitionVersionsById.get(processTaskDefinitionVersion.id);
    if (
      existing != null &&
      (hasResolvedFieldDefinitions(existing) || !hasResolvedFieldDefinitions(processTaskDefinitionVersion))
    ) {
      return;
    }
    this.fullProcessTaskDefinitionVersionsById.set(
      processTaskDefinitionVersion.id,
      processTaskDefinitionVersion
    );
  }

  private resolveFullProcessTaskDefinitionVersion(
    processTaskDefinitionVersionRef:
      | ResolvableProcessTaskDefinitionVersion
      | {__typename: 'InaccessibleInfoNode'}
      | null
  ): ResolvableProcessTaskDefinitionVersion | null {
    if (processTaskDefinitionVersionRef == null || isInaccessible(processTaskDefinitionVersionRef)) {
      return null;
    }

    const processTaskDefinitionVersion = this.fullProcessTaskDefinitionVersionsById.get(
      processTaskDefinitionVersionRef.id
    );
    if (processTaskDefinitionVersion != null) {
      return processTaskDefinitionVersion;
    }
    // Fall back to the inline ref when the map miss is from a PTDV that wasn't registered (e.g. a
    // standalone mapper-config ref that doesn't appear as a node PTDV or in availableTaskDefinitions).
    // The inline ref still satisfies the resolver contract if it carries field definitions.
    if (hasResolvedFieldDefinitions(processTaskDefinitionVersionRef)) {
      return processTaskDefinitionVersionRef;
    }
    logging.warn('Unable to resolve full process task definition version for flowchart mapper.', {
      extra: {
        processTaskDefinitionVersionId: processTaskDefinitionVersionRef.id,
        flowchartConfigVersionId: this.flowchartConfigVersion?.id,
      },
    });
    return null;
  }

  private isInaccessibleStaticTaskNodePTDV(ptdvId: string): boolean {
    return this.nodesStaticData.some(
      (node) =>
        isStaticTaskSchemaNodeData(node) &&
        node.isInaccessible &&
        node.taskDefinitionVersionSpec.clientId === ptdvId
    );
  }

  private isInaccessibleStaticTaskNode(nodeId: string): boolean {
    const node = this.nodesStaticData.find((staticNode) => staticNode.id === nodeId);
    return node != null && isStaticTaskSchemaNodeData(node) && node.isInaccessible === true;
  }

  /**
   * Builds a mapping from field identifiers to their client identifiers, to be used when forming
   * data flow edges.
   */
  private buildFieldIdentifierToClientIdMapping(): void {
    const nodeConfigs = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [];
    for (const nodeConfig of nodeConfigs) {
      const taskDefinitionVersion = this.nodeClientIdToTaskDefinitionVersion[nodeConfig.id];
      if (taskDefinitionVersion == null) {
        continue;
      }

      const inputFieldDefinitions = taskDefinitionVersion.inputFieldDefinitions ?? [];
      for (const fieldDefinition of inputFieldDefinitions) {
        this.fieldIdentifierToFieldClientId[fieldDefinition.fieldIdentifier] =
          fieldDefinition.fieldClientIdentifier;
      }

      const outputFieldDefinitions = taskDefinitionVersion.outputFieldDefinitions ?? [];
      for (const fieldDefinition of outputFieldDefinitions) {
        this.fieldIdentifierToFieldClientId[fieldDefinition.fieldIdentifier] =
          fieldDefinition.fieldClientIdentifier;
      }
    }
  }

  private convertFlowchartConfigVersionToEdges(): void {
    this.controlFlowEdges = this.buildControlFlowEdges();
    // The read-only execution viewer renders flowchart structure (nodes + control flow) only and never
    // displays data flow edges, field sections, or the transitions sidepanel. It still computes the
    // minimal planned transitions from control-flow reachability (setFallbackTransitionSources), but it
    // skips schema-mapper transition hydration and all data-flow edge construction in that mode.
    if (!isExecutionViewerMode(this.editorMode)) {
      this.populateTransitionsFromSchemaMapperConfigs();
      const showDataFlowEdges =
        FEATURE_OVERRIDES_FOR_EDITOR_MODE[this.editorMode][
          ProcessFlowchartEditorModeFeatureType.SHOW_NODE_SECTIONS_AND_DATA_FLOW_EDGES
        ];
      if (showDataFlowEdges) {
        this.dataFlowEdges = this.buildDataFlowEdges();
        this.dataFlowEdges = this.dataFlowEdges.filter((edge) => this.doesDataFlowEdgeHaveValidHandles(edge));
      }
    }
    this.edgesStaticData = [...this.controlFlowEdges, ...this.dataFlowEdges];
    if (
      this.flowchartEditorConfigurator.hasFeature(ProcessFlowchartPlatformFeatureType.ALLOW_MAP_FROM_START)
    ) {
      this.updateNodesWithMapFromStartFields();
    }
  }

  /**
   * Check if a data flow edge's handles exist on the source and target nodes.
   */
  private doesDataFlowEdgeHaveValidHandles(edge: FlowchartStaticEdgeData): boolean {
    if (edge.type !== ProcessFlowchartEdgeType.DATA_FLOW || !edge.sourceHandle || !edge.targetHandle) {
      return false;
    }

    const sourceNode = this.getNodeById(edge.source);
    const targetNode = this.getNodeById(edge.target);

    if (
      !sourceNode ||
      !targetNode ||
      !isStaticTaskSchemaNodeData(sourceNode) ||
      !isStaticTaskSchemaNodeData(targetNode)
    ) {
      return false;
    }

    const sourceHandleInfo = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle);
    const targetHandleInfo = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle);

    return (
      this.doesFieldExistOnNode(sourceNode, sourceHandleInfo.fieldClientId, sourceHandleInfo.sectionType) &&
      this.doesFieldExistOnNode(targetNode, targetHandleInfo.fieldClientId, targetHandleInfo.sectionType)
    );
  }

  /**
   * Check if a field exists on a node's field definitions and is not archived.
   */
  private doesFieldExistOnNode(
    node: TaskSchemaStaticNodeData,
    fieldClientId: string,
    sectionType: TaskSchemaNodeSectionType
  ): boolean {
    const isOutputNode = node.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS;
    const isOutputSection = sectionType === TaskSchemaNodeSectionType.OUTPUTS;
    const isMapFromStartSection = sectionType === TaskSchemaNodeSectionType.MAP_FROM_START;

    if (isMapFromStartSection) {
      const rootNode = this.getRootNode();
      if (!rootNode) {
        return false;
      }
      const rootFieldDefinitions = rootNode.taskDefinitionVersionSpec.inputFieldDefinitions;
      if (!rootFieldDefinitions) {
        return false;
      }
      return rootFieldDefinitions.some(
        (field) =>
          field.fieldClientIdentifier === fieldClientId &&
          (field.fieldDefinition.archiveData == null || !field.fieldDefinition.archiveData.archived)
      );
    }

    const fieldDefinitions =
      isOutputNode || isOutputSection
        ? node.taskDefinitionVersionSpec.outputFieldDefinitions
        : node.taskDefinitionVersionSpec.inputFieldDefinitions;

    if (!fieldDefinitions) {
      return false;
    }

    // Check that the field exists and is not archived
    return fieldDefinitions.some(
      (field) =>
        field.fieldClientIdentifier === fieldClientId &&
        (field.fieldDefinition.archiveData == null || !field.fieldDefinition.archiveData.archived)
    );
  }

  private buildControlFlowEdges(): Array<FlowchartStaticEdgeData> {
    return (
      this.flowchartConfigVersion?.templateFlowchart?.edgeConfigs.map((edge) => {
        const newEdge = buildControlFlowEdge({
          clientId: edge.id,
          sourceNodeId: edge.fromNodeConfig.id,
          targetNodeId: edge.toNodeConfig.id,
          label:
            this.nodeIdPairToEdgeLabel[
              getNodeIdPairKeyForEdgeLabel(edge.fromNodeConfig.id, edge.toNodeConfig.id)
            ],
        });
        this.cidToIdMap[edge.id] = edge.id;
        return newEdge;
      }) ?? []
    );
  }

  private buildDataFlowEdges(): Array<FlowchartStaticEdgeData> {
    const nodeConfigs = this.flowchartConfigVersion?.templateFlowchart?.nodeConfigs ?? [];
    const dataFlowEdges: Array<FlowchartStaticEdgeData> = [];
    for (const nodeConfig of nodeConfigs) {
      for (const inboundSchemaMapper of nodeConfig.inboundSchemaMappers) {
        for (const schemaMapperDependency of inboundSchemaMapper.schemaMapperDependencies ?? []) {
          dataFlowEdges.push(
            ...compact(
              (schemaMapperDependency.fieldMapperConfigs ?? []).map((fieldMapperConfig) =>
                this.buildDataFlowEdgeFromFieldMapperConfig(
                  fieldMapperConfig,
                  schemaMapperDependency,
                  inboundSchemaMapper
                )
              )
            )
          );
        }
      }
    }
    return dataFlowEdges;
  }

  private buildDataFlowEdgeFromFieldMapperConfig(
    fieldMapperConfig: FieldMapperConfigGQL,
    schemaMapperDependency: SchemaMapperDependencyGQL,
    inboundSchemaMapper: InboundSchemaMapperGQL
  ): FlowchartStaticEdgeData | null {
    const fromNodeId = schemaMapperDependency.fromNodeConfig.id;
    const toNodeId = inboundSchemaMapper.toNodeConfig.id;
    if (this.isInaccessibleStaticTaskNode(fromNodeId) || this.isInaccessibleStaticTaskNode(toNodeId)) {
      return null;
    }

    const fromTaskDefinitionVersion = this.resolveFullProcessTaskDefinitionVersion(
      fieldMapperConfig.fromProcessTaskDefinitionVersion
    );
    if (fromTaskDefinitionVersion == null) {
      return null;
    }
    const fromFieldInfo = this.getUpstreamMappingInfoFromFieldMapperConfig(
      fieldMapperConfig,
      fromTaskDefinitionVersion
    );
    if (fromFieldInfo == null) {
      return null;
    }
    if (fromFieldInfo.fromFieldSectionType === TaskSchemaNodeSectionType.MAP_FROM_START) {
      if (this.mapFromStartFieldIdsByNode[fromNodeId]) {
        if (!this.mapFromStartFieldIdsByNode[fromNodeId].includes(fromFieldInfo.fromFieldId)) {
          this.mapFromStartFieldIdsByNode[fromNodeId].push(fromFieldInfo.fromFieldId);
        }
      } else {
        this.mapFromStartFieldIdsByNode[fromNodeId] = [fromFieldInfo.fromFieldId];
      }
    }

    const mapsToType = inboundSchemaMapper.schemaMapperConfig?.mapsToType;
    if (mapsToType == null) {
      return null;
    }

    const defaultToFieldSectionType =
      mapsToType === ProcessMapperTokenType.PROCESS_OUTPUT
        ? TaskSchemaNodeSectionType.OUTPUTS
        : TaskSchemaNodeSectionType.INPUTS;
    const toFieldId = fieldMapperConfig.toProcessFieldDefinition.fieldIdentifier;
    // Node PTDVs from the editor query's templateFlowchart are slim (no output field definitions), so
    // resolve to the full version before checking root output fields.
    const targetPTDV = this.resolveFullProcessTaskDefinitionVersion(
      this.nodeClientIdToTaskDefinitionVersion[toNodeId] ?? null
    );
    const rootOutputFieldIds = targetPTDV ? this.getRootOutputFieldIds(targetPTDV) : null;
    const toFieldSectionType =
      rootOutputFieldIds != null && rootOutputFieldIds.has(toFieldId)
        ? TaskSchemaNodeSectionType.OUTPUTS
        : defaultToFieldSectionType;
    // Fall back to the raw field identifier when a client id isn't registered (e.g. stale/archived/
    // deleted fields, or slim PTDVs without field definitions) so edge handles stay stable.
    const toFieldClientId = this.fieldIdentifierToFieldClientId[toFieldId] ?? toFieldId;

    const {fromFieldSectionType, fromFieldId} = fromFieldInfo;
    const fromFieldClientId = this.fieldIdentifierToFieldClientId[fromFieldId] ?? fromFieldId;

    return buildDataFlowEdge({
      sourceNodeId: fromNodeId,
      targetNodeId: toNodeId,
      fromFieldId: fromFieldClientId,
      fromFieldSectionType,
      toFieldId: toFieldClientId,
      toFieldSectionType,
    });
  }

  private buildCustomMappingData(
    fieldMapperConfig: FieldMapperConfigGQL | TopLevelFieldMapperConfigGQL,
    fromFieldId: string
  ): CustomMappingData | null {
    const customMappingData: CustomMappingData = {};
    if (fieldMapperConfig.resultMustBeNonNullToMap) {
      customMappingData.resultMustBeNonNullToMap = true;
    }
    if (isLookupConfigCustomMapping(fieldMapperConfig.lookupConfig, fromFieldId)) {
      customMappingData.lookupConfig = {
        isMulti: fieldMapperConfig.lookupConfig.isMulti,
        lookupSteps: fieldMapperConfig.lookupConfig.lookupSteps,
        multiFlattenLevel: fieldMapperConfig.lookupConfig.multiFlattenLevel,
      };
    }
    return isCustomMappingDataEmpty(customMappingData) ? null : customMappingData;
  }

  private setTransitionSource(
    transition: Transition,
    sourcePTDVId: string,
    transitionSource: TransitionSource
  ): void {
    const existingSource = transition.transitionSourcesBySourcePTDVId[sourcePTDVId];
    if (existingSource != null && existingSource !== transitionSource) {
      assert(false, 'Expected existing transition source to match inferred transition source', {
        existingSource,
        transitionSource,
        sourcePTDVId,
      });
    }
    transition.transitionSourcesBySourcePTDVId[sourcePTDVId] = transitionSource;
  }

  private getUpstreamMappingInfoFromFieldMapperConfig(
    fieldMapperConfig: FieldMapperConfigGQL | TopLevelFieldMapperConfigGQL,
    fromTaskDefinitionVersion: ResolvableProcessTaskDefinitionVersion
  ): {
    fromFieldSectionType: TaskSchemaNodeSectionType;
    fromFieldId: string;
  } | null {
    const inputFieldDefinitions = fromTaskDefinitionVersion.inputFieldDefinitions ?? [];
    const outputFieldDefinitions = fromTaskDefinitionVersion.outputFieldDefinitions ?? [];

    // A slim PTDV reference (e.g. an optional-transition source/target that is neither a flowchart
    // node nor an available task definition) never selected its field definitions, so there is
    // nothing to validate the source field against.
    const canValidateAgainstFieldDefinitions = hasResolvedFieldDefinitions(fromTaskDefinitionVersion);

    // Examine lookup steps to find the field this edge is mapping from. Workflows after the introduction
    // of task definitions will have a PROCESS_TASK_DEFINITION_FIELD lookup step, while Workflows created
    // before that change will have a SCHEMA_FIELD lookup step.
    const fromFieldId = getFirstFieldIdFromLookupConfig(fieldMapperConfig.lookupConfig);
    if (fromFieldId == null) {
      return null;
    }

    const isFromFieldInFieldDefinitions = (fieldDefinitions: Array<FieldDefinitionGraphQL>): boolean =>
      fieldDefinitions.some((fieldDefinition) => fieldDefinition.fieldIdentifier === fromFieldId);

    const throwCouldNotDetermineSectionError = (): never => {
      assert(false, `Could not determine the section of the source field`, {
        fieldMapperConfig,
      });
    };

    let fromFieldSectionType!: TaskSchemaNodeSectionType;
    const lookupSteps: Array<LookupStepSpec> = fieldMapperConfig.lookupConfig.lookupSteps;
    if (lookupSteps.some((lookupStep) => lookupStep.type === LookupStepType.FLOWCHART_PROCESS_TASK)) {
      return {
        fromFieldSectionType: TaskSchemaNodeSectionType.MAP_FROM_START,
        fromFieldId,
      };
    }
    if (fieldMapperConfig.mapsFromType === ProcessMapperTokenType.PROCESS_OUTPUT) {
      if (lookupSteps[0].type === LookupStepType.PROCESS_TASK) {
        assert(
          !canValidateAgainstFieldDefinitions || isFromFieldInFieldDefinitions(inputFieldDefinitions),
          'Expected the source field to be a valid field in the task schema.'
        );
        fromFieldSectionType = TaskSchemaNodeSectionType.INPUTS;
      } else if (
        !canValidateAgainstFieldDefinitions ||
        isFromFieldInFieldDefinitions(outputFieldDefinitions)
      ) {
        fromFieldSectionType = TaskSchemaNodeSectionType.OUTPUTS;
      } else {
        throwCouldNotDetermineSectionError();
      }
    } else if (!canValidateAgainstFieldDefinitions || isFromFieldInFieldDefinitions(inputFieldDefinitions)) {
      fromFieldSectionType = TaskSchemaNodeSectionType.INPUTS;
    } else {
      throwCouldNotDetermineSectionError();
    }

    return {fromFieldSectionType, fromFieldId};
  }

  private buildFieldIdToClientIdFromPTDV(
    ptdv: {
      inputFieldDefinitions?: ReadonlyArray<{fieldIdentifier: string; fieldClientIdentifier: string}> | null;
      outputFieldDefinitions?: ReadonlyArray<{fieldIdentifier: string; fieldClientIdentifier: string}> | null;
    },
    fieldIdToClientId: Record<string, string>
  ): void {
    for (const fieldDef of ptdv.inputFieldDefinitions ?? []) {
      if (fieldIdToClientId[fieldDef.fieldIdentifier] == null) {
        fieldIdToClientId[fieldDef.fieldIdentifier] = fieldDef.fieldClientIdentifier;
      }
    }
    for (const fieldDef of ptdv.outputFieldDefinitions ?? []) {
      if (fieldIdToClientId[fieldDef.fieldIdentifier] == null) {
        fieldIdToClientId[fieldDef.fieldIdentifier] = fieldDef.fieldClientIdentifier;
      }
    }
  }

  /**
   * Populates transitions and their customMappingDataByFieldPair from the top-level
   * schemaMapperConfigs. Covers all configs — both those referenced by node inboundSchemaMappers
   * (planned transitions) and unreferenced ones (optional transitions with no control flow edge).
   * Field client IDs are resolved from PTDVs in the fullProcessTaskDefinitionVersionsById map.
   */
  private populateTransitionsFromSchemaMapperConfigs(): void {
    const schemaMapperConfigs = this.flowchartConfigVersion?.schemaMapperConfigs ?? [];

    for (const schemaMapperConfig of schemaMapperConfigs) {
      if (isInaccessible(schemaMapperConfig.toProcessTaskDefinitionVersion)) {
        continue;
      }

      const targetPTDVRef = schemaMapperConfig.toProcessTaskDefinitionVersion;
      const targetPTDV = this.resolveFullProcessTaskDefinitionVersion(targetPTDVRef);
      if (targetPTDV == null) {
        continue;
      }
      const targetPTDVId = targetPTDV.id;

      const sourceInfos: Array<{ptdvId: string; mapsFromType: ProcessMapperTokenType}> = [];
      for (const sourceConfig of schemaMapperConfig.schemaMapperSourceConfigs) {
        if (
          !isInaccessible(sourceConfig.processTaskDefinitionVersion) &&
          !this.isInaccessibleStaticTaskNodePTDV(sourceConfig.processTaskDefinitionVersion.id)
        ) {
          sourceInfos.push({
            ptdvId: sourceConfig.processTaskDefinitionVersion.id,
            mapsFromType: sourceConfig.mapsFromType,
          });
        }
      }
      if (sourceInfos.length === 0) {
        continue;
      }

      const sourcePTDVIds = sourceInfos.map((info) => info.ptdvId);
      const transition = this.getOrCreateTransition(sourcePTDVIds, targetPTDVId);

      for (const {ptdvId, mapsFromType} of sourceInfos) {
        const transitionSource = this.getTransitionSourceFromMapperTokenType(mapsFromType);
        this.setTransitionSource(transition, ptdvId, transitionSource);
      }

      const localFieldIdToClientId: Record<string, string> = {};
      this.buildFieldIdToClientIdFromPTDV(targetPTDV, localFieldIdToClientId);
      for (const fieldMapperConfig of schemaMapperConfig.schemaFieldMapperConfigs) {
        const fromPTDV = this.resolveFullProcessTaskDefinitionVersion(
          fieldMapperConfig.fromProcessTaskDefinitionVersion
        );
        if (fromPTDV != null && !this.isInaccessibleStaticTaskNodePTDV(fromPTDV.id)) {
          this.buildFieldIdToClientIdFromPTDV(fromPTDV, localFieldIdToClientId);
        }
      }

      const defaultToFieldSectionType =
        schemaMapperConfig.mapsToType === ProcessMapperTokenType.PROCESS_OUTPUT
          ? TaskSchemaNodeSectionType.OUTPUTS
          : TaskSchemaNodeSectionType.INPUTS;
      const rootOutputFieldIds = this.getRootOutputFieldIds(targetPTDV);

      for (const fieldMapperConfig of schemaMapperConfig.schemaFieldMapperConfigs) {
        const fromTaskDefinitionVersion = this.resolveFullProcessTaskDefinitionVersion(
          fieldMapperConfig.fromProcessTaskDefinitionVersion
        );
        if (
          fromTaskDefinitionVersion == null ||
          this.isInaccessibleStaticTaskNodePTDV(fromTaskDefinitionVersion.id)
        ) {
          continue;
        }
        const fromFieldInfo = this.getUpstreamMappingInfoFromFieldMapperConfig(
          fieldMapperConfig,
          fromTaskDefinitionVersion
        );
        if (fromFieldInfo == null) {
          continue;
        }

        const sourcePTDVId = fromTaskDefinitionVersion.id;
        const {fromFieldSectionType, fromFieldId} = fromFieldInfo;
        const fromFieldClientId = localFieldIdToClientId[fromFieldId] ?? fromFieldId;
        const toFieldId = fieldMapperConfig.toProcessFieldDefinition.fieldIdentifier;
        const toFieldClientId = localFieldIdToClientId[toFieldId] ?? toFieldId;

        const toFieldSectionType =
          rootOutputFieldIds != null && rootOutputFieldIds.has(toFieldId)
            ? TaskSchemaNodeSectionType.OUTPUTS
            : defaultToFieldSectionType;

        const fieldPairKey = getFieldPairKey({
          sourcePTDVId,
          sourceSectionType: fromFieldSectionType,
          sourceFieldClientId: fromFieldClientId,
          targetPTDVId,
          targetSectionType: toFieldSectionType,
          targetFieldClientId: toFieldClientId,
        });

        const customMappingData = this.buildCustomMappingData(fieldMapperConfig, fromFieldId);
        transition.customMappingDataByFieldPair[fieldPairKey] = customMappingData;
      }
    }
  }

  private setFallbackTransitionSources(): void {
    for (const node of this.nodesStaticData) {
      if (!isStaticTaskSchemaNodeData(node)) {
        continue;
      }

      const downstreamMappableNodeIds = findAllDownstreamMappableNodeIds(
        {nodes: this.nodesStaticData.map((n) => ({id: n.id, data: n})), edges: this.edgesStaticData},
        node.id
      );

      for (const downstreamMappableNodeId of downstreamMappableNodeIds) {
        const downstreamNode = this.getTaskSchemaNodeById(downstreamMappableNodeId);
        if (!downstreamNode) {
          continue;
        }

        const sourcePTDVId = node.taskDefinitionVersionSpec.clientId;
        const targetPTDVId = downstreamNode.taskDefinitionVersionSpec.clientId;

        const transition = this.getOrCreateTransition([sourcePTDVId], targetPTDVId);

        // Set default source if not already set by a mapper config
        if (transition.transitionSourcesBySourcePTDVId[sourcePTDVId] == null) {
          const defaultSource = getDefaultTransitionSource(node.id, this.nodesStaticData);
          transition.transitionSourcesBySourcePTDVId[sourcePTDVId] = defaultSource;
        }
      }
    }
  }

  /**
   * Marks transitions as optional (isPlanned = false) if their PTDV pairs are not
   * connected by control flow edges in the flowchart.
   */
  private markOptionalTransitions(): void {
    const connectedPTDVPairs = new Set<string>();
    for (const node of this.nodesStaticData) {
      if (!isStaticTaskSchemaNodeData(node) || !isMappableNode({id: node.id, data: node})) {
        continue;
      }
      const upstreamIds = findAllUpstreamMappableNodeIds(
        {nodes: this.nodesStaticData.map((n) => ({id: n.id, data: n})), edges: this.edgesStaticData},
        node.id
      );
      for (const upstreamId of upstreamIds) {
        const upstreamPTDV = this.nodeClientIdToTaskDefinitionVersion[upstreamId]?.id;
        const nodePTDV = this.nodeClientIdToTaskDefinitionVersion[node.id]?.id;
        if (upstreamPTDV && nodePTDV) {
          connectedPTDVPairs.add(`${upstreamPTDV}:${nodePTDV}`);
        }
      }
    }

    for (const transition of Object.values(this.transitions)) {
      const allPairsConnected = transition.sourcePTDVIds.every((sourcePTDVId) =>
        connectedPTDVPairs.has(`${sourcePTDVId}:${transition.targetPTDVId}`)
      );
      if (!allPairsConnected) {
        transition.isPlanned = false;
      }
    }
  }

  private getTransitionSourceFromMapperTokenType(mapperTokenType: ProcessMapperTokenType): TransitionSource {
    return mapperTokenType === ProcessMapperTokenType.PROCESS_OUTPUT
      ? TransitionSource.OUTPUTS
      : TransitionSource.INPUTS;
  }

  /**
   * For START->END transitions, the root PTDV is shared by both the START and END nodes.
   * The default mapsToType-based logic assigns INPUTS to all target fields, but output
   * fields on the END node belong to OUTPUTS. Returns the set of output field identifiers
   * for the root PTDV if applicable, or null for non-root PTDVs.
   */
  private getRootOutputFieldIds(
    targetPTDV: Pick<
      ResolvableProcessTaskDefinitionVersion,
      'processTaskDefinition' | 'outputFieldDefinitions'
    >
  ): Set<string> | null {
    if (
      targetPTDV.processTaskDefinition?.id !== this.rootTaskDefinition.id ||
      this.rootTaskSchema.outputSchema == null
    ) {
      return null;
    }
    return new Set((targetPTDV.outputFieldDefinitions ?? []).map((fd) => fd.fieldIdentifier));
  }

  private updateNodesWithMapFromStartFields(): void {
    const rootNodeData = this.getRootNode();
    if (!rootNodeData) {
      throw new Error('No root node found');
    }
    populateMapFromStartSectionRows(this.nodesStaticData, rootNodeData, this.mapFromStartFieldIdsByNode);
  }

  private getDidInitialConfigHaveConditionalMapping(): boolean {
    return (this.flowchartConfigVersion?.schemaMapperConfigs ?? []).some((config) =>
      config.schemaFieldMapperConfigs.some((fmc) => fmc.resultMustBeNonNullToMap)
    );
  }
}

export default function buildStaticDataFromFlowchartConfigGraphQL(
  args: BuildStaticDataFromFlowchartConfigGraphQLRunnerArgs
): StaticFlowchartData {
  const runner = new BuildStaticDataFromFlowchartConfigGraphQLRunner(args);
  return runner.run();
}
